import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Mail,
  Calendar,
  Clock,
  User,
  FileText,
  Paperclip,
  Send,
  AlertTriangle,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  ExternalLink,
  MessageSquare,
} from "lucide-react";
import {
  Ticket,
  TicketStatus,
  TicketPriority,
  useUpdateTicketStatus,
} from "../../../lib/api/tickets";
import TicketResponseModal from "./TicketResponseModal";

interface TicketDetailsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: Ticket | null;
  onTicketUpdated: () => void;
}

const TicketDetailsSidebar: React.FC<TicketDetailsSidebarProps> = ({
  isOpen,
  onClose,
  ticket,
  onTicketUpdated,
}) => {
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const { mutate: updateStatus, isPending: isUpdatingStatus } =
    useUpdateTicketStatus();

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        !isResponseModalOpen
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, isResponseModalOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isResponseModalOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose, isResponseModalOpen]);

  const handleStatusChange = (newStatus: TicketStatus) => {
    if (!ticket) return;
    updateStatus(
      { ticketId: ticket._id, status: newStatus },
      {
        onSuccess: () => {
          onTicketUpdated();
        },
      }
    );
  };

  const handleResponseSent = () => {
    setIsResponseModalOpen(false);
    onTicketUpdated();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getStatusConfig = (status: TicketStatus) => {
    const configs = {
      open: {
        bg: "bg-blue-50",
        text: "text-blue-700",
        border: "border-blue-200",
        icon: AlertCircle,
        label: "Open",
      },
      in_progress: {
        bg: "bg-amber-50",
        text: "text-amber-700",
        border: "border-amber-200",
        icon: Clock,
        label: "In Progress",
      },
      resolved: {
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        border: "border-emerald-200",
        icon: CheckCircle,
        label: "Resolved",
      },
      closed: {
        bg: "bg-gray-100",
        text: "text-gray-600",
        border: "border-gray-200",
        icon: XCircle,
        label: "Closed",
      },
    };
    return configs[status] || configs.open;
  };

  const getPriorityConfig = (priority: TicketPriority) => {
    const configs = {
      low: {
        bg: "bg-green-50",
        text: "text-green-700",
        border: "border-green-200",
        label: "Low",
      },
      medium: {
        bg: "bg-yellow-50",
        text: "text-yellow-700",
        border: "border-yellow-200",
        label: "Medium",
      },
      high: {
        bg: "bg-orange-50",
        text: "text-orange-700",
        border: "border-orange-200",
        label: "High",
      },
      critical: {
        bg: "bg-red-50",
        text: "text-red-700",
        border: "border-red-200",
        label: "Critical",
      },
    };
    return configs[priority] || configs.medium;
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      account: "Account",
      payment: "Payment",
      technical: "Technical",
      project: "Project",
      verification: "Verification",
      other: "Other",
    };
    return labels[category] || category;
  };

  if (!ticket) return null;

  const statusConfig = getStatusConfig(ticket.status);
  const priorityConfig = getPriorityConfig(ticket.priority);
  const StatusIcon = statusConfig.icon;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed top-0 right-0 h-full w-full sm:w-[500px] md:w-[600px] bg-white shadow-2xl z-[9999] transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center text-white">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Ticket Details
              </h2>
              <p className="text-sm text-[#8B52FF] font-mono">
                {ticket.ticketNumber}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
          {/* Status & Priority */}
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusConfig.bg} ${statusConfig.text}`}
            >
              <StatusIcon className="w-4 h-4" />
              {statusConfig.label}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${priorityConfig.bg} ${priorityConfig.text}`}
            >
              {ticket.priority === "critical" && (
                <AlertTriangle className="w-4 h-4" />
              )}
              {priorityConfig.label} Priority
            </span>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2">
            <select
              value={ticket.status}
              onChange={(e) =>
                handleStatusChange(e.target.value as TicketStatus)
              }
              disabled={isUpdatingStatus}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF]"
            >
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <button
              onClick={() => setIsResponseModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white text-sm font-medium rounded-lg hover:shadow-lg transition-all"
            >
              <Send className="w-4 h-4" />
              Send Response
            </button>
          </div>

          {/* Subject */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {ticket.subject}
            </h3>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(ticket.createdAt)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatTime(ticket.createdAt)}
              </span>
              <span className="capitalize px-2 py-0.5 bg-gray-100 rounded text-gray-600">
                {getCategoryLabel(ticket.category)}
              </span>
            </div>
          </div>

          {/* User Info */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Submitted By
            </h4>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center text-white font-medium">
                {ticket.userId?.firstname?.[0] || ticket.email[0].toUpperCase()}
              </div>
              <div>
                {ticket.userId ? (
                  <>
                    <p className="font-medium text-gray-900">
                      {ticket.userId.firstname} {ticket.userId.lastname}
                    </p>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {ticket.userId.email}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {ticket.email}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Description
            </h4>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {ticket.description}
              </p>
            </div>
          </div>

          {/* Attachments */}
          {ticket.attachments && ticket.attachments.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Attachments ({ticket.attachments.length})
              </h4>
              <div className="space-y-2">
                {ticket.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded bg-[#8B52FF]/10 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-[#8B52FF]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">
                          {attachment.originalName}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatFileSize(attachment.size)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <a
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                        title="View"
                      >
                        <ExternalLink className="w-4 h-4 text-gray-500" />
                      </a>
                      <a
                        href={attachment.url}
                        download={attachment.originalName}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4 text-gray-500" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Responses / Conversation */}
          {ticket.responses && ticket.responses.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Responses ({ticket.responses.length})
              </h4>
              <div className="space-y-4">
                {ticket.responses.map((response, index) => (
                  <div
                    key={response._id || index}
                    className="bg-gradient-to-br from-[#8B52FF]/5 to-[#E05A6A]/5 rounded-xl p-4 border border-[#8B52FF]/10"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center text-white text-sm font-medium">
                        {response.respondedByName?.[0] || "A"}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm">
                          {response.respondedByName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(response.createdAt)} at{" "}
                          {formatTime(response.createdAt)}
                        </p>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm whitespace-pre-wrap leading-relaxed">
                      {response.message}
                    </p>
                    {response.attachments &&
                      response.attachments.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex flex-wrap gap-2">
                            {response.attachments.map((att, i) => (
                              <a
                                key={i}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded text-xs text-gray-600 hover:bg-gray-50"
                              >
                                <Paperclip className="w-3 h-3" />
                                {att.originalName}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Assigned To */}
          {ticket.assignedTo && (
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Assigned To
              </h4>
              <p className="text-gray-900">
                {ticket.assignedTo.firstname} {ticket.assignedTo.lastname}
              </p>
              <p className="text-sm text-gray-500">{ticket.assignedTo.email}</p>
            </div>
          )}

          {/* Timestamps */}
          <div className="text-xs text-gray-400 space-y-1 pt-4 border-t border-gray-100">
            <p>Created: {new Date(ticket.createdAt).toLocaleString()}</p>
            <p>Last Updated: {new Date(ticket.updatedAt).toLocaleString()}</p>
            {ticket.resolvedAt && (
              <p>Resolved: {new Date(ticket.resolvedAt).toLocaleString()}</p>
            )}
            {ticket.closedAt && (
              <p>Closed: {new Date(ticket.closedAt).toLocaleString()}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-gray-100 bg-gray-50">
          <button
            onClick={() => setIsResponseModalOpen(true)}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl hover:shadow-lg transition-all"
          >
            <Send className="w-5 h-5" />
            Send Response to User
          </button>
        </div>
      </div>

      {/* Response Modal */}
      <TicketResponseModal
        isOpen={isResponseModalOpen}
        onClose={() => setIsResponseModalOpen(false)}
        ticket={ticket}
        onResponseSent={handleResponseSent}
      />
    </>
  );
};

export default TicketDetailsSidebar;
